'use client';
import React, { useState, useEffect } from 'react';

// Довідкові дані для розрахунків
const METAL_DENSITIES = {
  steel: 7.85, // г/см³
  aluminum: 2.7, // г/см³
  copper: 8.96, // г/см³
  brass: 8.5, // г/см³
  stainless: 7.9, // г/см³
};

const METAL_PRICES = {
  steel: 32000, // грн/т
  aluminum: 85000, // грн/т
  copper: 240000, // грн/т
  brass: 180000, // грн/т
  stainless: 120000, // грн/т
};

const PRODUCT_TYPES = {
  sheet: "Лист",
  pipe: "Труба",
  circle: "Круг",
  square: "Квадрат",
  channel: "Швелер",
  angle: "Кутик",
  beam: "Двутавр",
  hexagon: "Шестигранник",
};

const MetalCalculator = () => {
  const [metal, setMetal] = useState('steel');
  const [productType, setProductType] = useState('sheet');
  const [dimensions, setDimensions] = useState({
    length: 1000, // мм
    width: 1000, // мм
    thickness: 1, // мм
    diameter: 50, // мм
    wall: 2, // мм для труб
    side: 50, // мм для квадрата
    height: 100, // мм для швелера/двутавра
    flange: 50, // мм для швелера/двутавра
  });
  const [quantity, setQuantity] = useState(1);
  
  const [weight, setWeight] = useState(0);
  const [price, setPrice] = useState(0);
  
  // Розрахунок ваги та вартості
  useEffect(() => {
    let calculatedWeight = 0;
    
    // Формули розрахунку об'єму та ваги для різних типів
    switch (productType) {
      case 'sheet':
        // V = length * width * thickness
        calculatedWeight = 
          (dimensions.length / 1000) * (dimensions.width / 1000) * (dimensions.thickness / 1000) * 
          METAL_DENSITIES[metal] * 1000; // кг
        break;
        
      case 'pipe':
        // V = π * (D² - d²) * length / 4, де d = D - 2*wall
        const outerDiameter = dimensions.diameter / 1000; // м
        const innerDiameter = (dimensions.diameter - 2 * dimensions.wall) / 1000; // м
        calculatedWeight = 
          Math.PI * (Math.pow(outerDiameter, 2) - Math.pow(innerDiameter, 2)) / 4 * 
          (dimensions.length / 1000) * METAL_DENSITIES[metal] * 1000; // кг
        break;
        
      case 'circle':
        // V = π * D² * length / 4
        calculatedWeight = 
          Math.PI * Math.pow(dimensions.diameter / 1000, 2) / 4 * 
          (dimensions.length / 1000) * METAL_DENSITIES[metal] * 1000; // кг
        break;
        
      case 'square':
        // V = side² * length
        calculatedWeight = 
          Math.pow(dimensions.side / 1000, 2) * (dimensions.length / 1000) * 
          METAL_DENSITIES[metal] * 1000; // кг
        break;
        
      case 'hexagon':
        // V = (3√3/2) * side² * length
        calculatedWeight = 
          (3 * Math.sqrt(3) / 2) * Math.pow(dimensions.side / 1000, 2) * 
          (dimensions.length / 1000) * METAL_DENSITIES[metal] * 1000; // кг
        break;
      
      // Для швелера, кутика і двутавра спрощені формули
      case 'channel':
      case 'angle':
      case 'beam':
        // Спрощений розрахунок для прикладу
        calculatedWeight = 
          (dimensions.height / 1000) * (dimensions.flange / 1000) * 
          (dimensions.thickness / 1000) * (dimensions.length / 1000) * 2 * 
          METAL_DENSITIES[metal] * 1000; // кг
        break;
        
      default:
        calculatedWeight = 0;
    }
    
    // Врахування кількості
    calculatedWeight *= quantity;
    
    // Округлення до 3-х знаків після коми
    setWeight(Math.round(calculatedWeight * 1000) / 1000);
    
    // Розрахунок вартості
    const calculatedPrice = (calculatedWeight / 1000) * METAL_PRICES[metal];
    setPrice(Math.round(calculatedPrice * 100) / 100);
    
  }, [metal, productType, dimensions, quantity]);
  
  // Обробники змін полів
  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    setDimensions(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };
  
  const handleQuantityChange = (e) => {
    setQuantity(parseInt(e.target.value) || 1);
  };
  
  // Готуємо поля для введення розмірів в залежності від типу продукту
  const renderDimensionFields = () => {
    switch (productType) {
      case 'sheet':
        return (
          <>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Довжина (мм)</label>
              <input 
                type="number" 
                name="length" 
                value={dimensions.length} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Ширина (мм)</label>
              <input 
                type="number" 
                name="width" 
                value={dimensions.width} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Товщина (мм)</label>
              <input 
                type="number" 
                name="thickness" 
                value={dimensions.thickness} 
                onChange={handleDimensionChange} 
                min="0.1" 
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        );
        
      case 'pipe':
        return (
          <>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Довжина (мм)</label>
              <input 
                type="number" 
                name="length" 
                value={dimensions.length} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Діаметр (мм)</label>
              <input 
                type="number" 
                name="diameter" 
                value={dimensions.diameter} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Товщина стінки (мм)</label>
              <input 
                type="number" 
                name="wall" 
                value={dimensions.wall} 
                onChange={handleDimensionChange} 
                min="0.1" 
                step="0.1"
                max={dimensions.diameter / 2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        );
        
      case 'circle':
        return (
          <>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Довжина (мм)</label>
              <input 
                type="number" 
                name="length" 
                value={dimensions.length} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Діаметр (мм)</label>
              <input 
                type="number" 
                name="diameter" 
                value={dimensions.diameter} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        );
        
      case 'square':
        return (
          <>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Довжина (мм)</label>
              <input 
                type="number" 
                name="length" 
                value={dimensions.length} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Сторона (мм)</label>
              <input 
                type="number" 
                name="side" 
                value={dimensions.side} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        );
        
      case 'hexagon':
        return (
          <>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Довжина (мм)</label>
              <input 
                type="number" 
                name="length" 
                value={dimensions.length} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Розмір під ключ (мм)</label>
              <input 
                type="number" 
                name="side" 
                value={dimensions.side} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        );
        
      case 'channel':
      case 'angle':
      case 'beam':
        return (
          <>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Довжина (мм)</label>
              <input 
                type="number" 
                name="length" 
                value={dimensions.length} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Висота (мм)</label>
              <input 
                type="number" 
                name="height" 
                value={dimensions.height} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Ширина полиці (мм)</label>
              <input 
                type="number" 
                name="flange" 
                value={dimensions.flange} 
                onChange={handleDimensionChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Товщина (мм)</label>
              <input 
                type="number" 
                name="thickness" 
                value={dimensions.thickness} 
                onChange={handleDimensionChange} 
                min="0.1" 
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="bg-gray-50 rounded-lg p-6 my-8 font-sans">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-semibold text-blue-700 mb-2">Калькулятор металу</h3>
        <p className="text-gray-600">Розрахуйте вагу та вартість металевих виробів</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div>
            <h4 className="text-lg font-medium text-gray-700 mb-3">Тип металу</h4>
            <div className="relative">
              <select 
                value={metal} 
                onChange={(e) => setMetal(e.target.value)}
                className="w-full appearance-none px-4 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="steel">Сталь</option>
                <option value="aluminum">Алюміній</option>
                <option value="copper">Мідь</option>
                <option value="brass">Латунь</option>
                <option value="stainless">Нержавіюча сталь</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-lg font-medium text-gray-700 mb-3">Тип виробу</h4>
            <div className="relative">
              <select 
                value={productType} 
                onChange={(e) => setProductType(e.target.value)}
                className="w-full appearance-none px-4 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(PRODUCT_TYPES).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-lg font-medium text-gray-700 mb-3">Розміри</h4>
            <div className="grid grid-cols-2 gap-4">
              {renderDimensionFields()}
            </div>
          </div>
          
          <div>
            <h4 className="text-lg font-medium text-gray-700 mb-3">Кількість</h4>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Кількість (шт)</label>
              <input 
                type="number" 
                value={quantity} 
                onChange={handleQuantityChange} 
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-700 mb-2">Вага</h4>
            <div className="text-3xl font-bold text-blue-600">
              {weight.toLocaleString('uk-UA')} <span className="text-xl">кг</span>
            </div>
          </div>
          
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-700 mb-2">Орієнтовна вартість</h4>
            <div className="text-3xl font-bold text-blue-600">
              {price.toLocaleString('uk-UA')} <span className="text-xl">грн</span>
            </div>
            <div className="mt-2 text-sm text-gray-500">
              Ціна за тонну: {METAL_PRICES[metal].toLocaleString('uk-UA')} грн/т
            </div>
          </div>
          
          <div className="space-y-3">
            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-md font-medium transition-colors">
              Запит на розрахунок ціни
            </button>
            <button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-md font-medium transition-colors">
              Додати до кошика
            </button>
          </div>
        </div>
      </div>
      
      <div className="mt-6 text-sm text-gray-500 text-center">
        <p>Примітка: Розрахунки є приблизними і можуть відрізнятися від фактичних. Для точного розрахунку зверніться до наших менеджерів.</p>
      </div>
    </div>
  );
};

export default MetalCalculator;